import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import config from '../../../config';

// HR Payslip — A4-sized printable view. The route ships data via the
// composite Payslip endpoint so we render once and call window.print().
// User picks "Save as PDF" in the browser print dialog when they want a
// stored copy — matches the existing invoice flow in this app.
export default function Payslip() {
  const { payrollId } = useParams();
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'error' });

  useEffect(() => {
    if (!payrollId || !companyId) return;
    (async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Hr/Payslip/${payrollId}/${companyId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error();
        setData(await res.json());
      } catch (e) {
        setSnack({ open: true, msg: 'Failed to load payslip', sev: 'error' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payrollId, companyId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data || !data.payroll) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ color: '#94a3b8' }}>
          Payslip not found.
        </Typography>
      </Box>
    );
  }

  const p = data.payroll;
  const e = data.employee || {};
  const monthName = new Date(p.year, p.month - 1, 1).toLocaleString('en-IN', {
    month: 'long',
  });
  const inr = (v) => '₹ ' + Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const logoUrl = data.hotelLogo
    ? data.hotelLogo.startsWith('http')
      ? data.hotelLogo
      : `${config.API_BASE_URL}${data.hotelLogo}`
    : null;

  const earnings = [
    ['Basic Salary',     p.basicSalary],
    ['HRA',              p.hra],
    ['Conveyance',       p.conveyance],
    ['Food Allowance',   p.foodAllowance],
    ['Overtime',         p.overtimeAmount],
    ['Incentive',        p.incentive],
    ['Bonus',            p.bonus],
    ['Service Share',    p.serviceShare],
    ['Other Earnings',   p.otherEarnings],
  ];
  const deductions = [
    ['Advance Recovery', p.advanceRecovery],
    ['Loan EMI',         p.loanEmi],
    ['Provident Fund',   p.pf],
    ['ESI',              p.esi],
    ['TDS',              p.tds],
    ['Professional Tax', p.pt],
    ['Other Deductions', p.otherDeductions],
  ];

  return (
    <Box sx={{ bgcolor: '#e2e8f0', minHeight: '100vh', py: 3 }}>
      {/* Toolbar (hidden in print) */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          mb: 2,
          display: 'flex',
          gap: 1.5,
          justifyContent: 'space-between',
          '@media print': { display: 'none' },
        }}>
        <Button
          variant='outlined'
          startIcon={<ArrowBackIcon />}
          onClick={() => window.history.back()}>
          Back
        </Button>
        <Button
          variant='contained'
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ bgcolor: '#1A56DB' }}>
          Print / Save PDF
        </Button>
      </Box>

      {/* Print stylesheet */}
      <style>{`
        @page { size: A4; margin: 12mm 10mm; }
        @media print {
          body { background: #fff !important; }
        }
        .payslip-sheet { background: #fff; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
        .payslip-sheet th, .payslip-sheet td { padding: 6px 10px; font-size: 12px; }
      `}</style>

      <Box
        className='payslip-sheet'
        sx={{
          maxWidth: 900,
          mx: 'auto',
          p: 4,
          border: '2px solid #1A56DB',
        }}>
        {/* Header */}
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{
            borderBottom: '2px solid #1A56DB',
            pb: 2,
            mb: 2,
          }}>
          {logoUrl && (
            <Box
              component='img'
              src={logoUrl}
              alt='logo'
              sx={{ width: 60, height: 60, objectFit: 'contain' }}
              onError={(ev) => {
                ev.target.style.display = 'none';
              }}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#1A56DB' }}>
              {data.hotelName || company?.name || 'Company'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
              {data.hotelAddress || ''}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1A56DB' }}>
              PAYSLIP
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {monthName} {p.year}
            </Typography>
          </Box>
        </Stack>

        {/* Employee info */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 2,
            border: '1px solid #cbd5e1',
            p: 1.5,
            borderRadius: 1,
            bgcolor: '#f8fafc',
          }}>
          {[
            ['Employee Code',  e.employeeCode],
            ['Employee Name',  e.employeeName],
            ['Department',     e.departmentName || '—'],
            ['Designation',    e.designationName || '—'],
            ['Joining Date',
              e.joiningDate
                ? new Date(e.joiningDate).toLocaleDateString('en-IN')
                : '—'],
            ['Bank A/C',       e.bankAccountNo || '—'],
            ['PAN',            e.pan || '—'],
            ['Aadhaar',        e.aadhaar || '—'],
          ].map(([k, v]) => (
            <Box key={k}>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748B', textTransform: 'uppercase' }}>
                {k}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                {v}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Attendance summary */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 1,
            mb: 2,
            border: '1px solid #cbd5e1',
            borderRadius: 1,
            overflow: 'hidden',
          }}>
          {[
            ['Present',   p.presentDays,    '#16a34a'],
            ['Half',      0,                '#ea580c'],
            ['Absent',    p.absentDays,     '#dc2626'],
            ['Leave',     p.leaveDays,      '#2563eb'],
            ['Holiday',   p.holidayDays,    '#0891b2'],
            ['OT Hours',  p.otHours,        '#f59e0b'],
          ].map(([k, v, c]) => (
            <Box
              key={k}
              sx={{
                p: 1,
                textAlign: 'center',
                borderRight: '1px solid #cbd5e1',
                '&:last-of-type': { borderRight: 'none' },
              }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                {k}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: c }}>{v}</Typography>
            </Box>
          ))}
        </Box>

        {/* Earnings + Deductions side by side */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            mb: 2,
          }}>
          {/* Earnings */}
          <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 1 }}>
            <Box
              sx={{
                bgcolor: '#16a34a',
                color: '#fff',
                px: 1.5,
                py: 0.75,
                fontWeight: 700,
                fontSize: '0.85rem',
              }}>
              EARNINGS
            </Box>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {earnings.map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ color: '#475569' }}>{k}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {inr(v)}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#dcfce7' }}>
                  <td style={{ fontWeight: 800, color: '#166534' }}>
                    Total Earnings
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 800, color: '#166534' }}>
                    {inr(p.totalEarnings)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>

          {/* Deductions */}
          <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 1 }}>
            <Box
              sx={{
                bgcolor: '#dc2626',
                color: '#fff',
                px: 1.5,
                py: 0.75,
                fontWeight: 700,
                fontSize: '0.85rem',
              }}>
              DEDUCTIONS
            </Box>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {deductions.map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ color: '#475569' }}>{k}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {inr(v)}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#fee2e2' }}>
                  <td style={{ fontWeight: 800, color: '#991b1b' }}>
                    Total Deductions
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 800, color: '#991b1b' }}>
                    {inr(p.totalDeductions)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Net salary band */}
        <Box
          sx={{
            bgcolor: '#1A56DB',
            color: '#fff',
            p: 2,
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', opacity: 0.85 }}>
              NET SALARY
            </Typography>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 900 }}>
              {inr(p.netSalary)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.75rem', opacity: 0.85 }}>
              PAID · BALANCE
            </Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
              {inr(p.paidAmount)} · {inr(p.balancePayable)}
            </Typography>
          </Box>
        </Box>

        {/* Payment ledger */}
        {data.payments?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
              Payment Details
            </Typography>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #cbd5e1' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Mode</th>
                  <th style={{ textAlign: 'left' }}>Reference</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((pay) => (
                  <tr key={pay.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td>{new Date(pay.paymentDate).toLocaleDateString('en-IN')}</td>
                    <td>{pay.payMode}</td>
                    <td>{pay.referenceNo || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {inr(pay.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        {/* Footer / signatures */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 4,
            mt: 4,
          }}>
          <Box
            sx={{
              borderTop: '1px solid #000',
              pt: 1,
              textAlign: 'center',
              fontSize: '0.8rem',
            }}>
            Employee Signature
          </Box>
          <Box
            sx={{
              borderTop: '1px solid #000',
              pt: 1,
              textAlign: 'center',
              fontSize: '0.8rem',
            }}>
            Authorised Signatory
          </Box>
        </Box>
        <Typography
          sx={{
            mt: 3,
            textAlign: 'center',
            fontSize: '0.7rem',
            color: '#94a3b8',
          }}>
          This is a system-generated payslip. Any discrepancy should be
          reported within 7 days.
        </Typography>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
